import { corePost } from "@/services/coreApi"
import {
  ALGORITHM_PLANROUTE_ANALYSER,
  buildCoreRequest,
} from "@/utils/coreRequestBuilder"
import { computeTotalConsumption } from "@/utils/RouteProcessing"
import { isFuelOptimisedRequest } from "@/utils/routeHelper"
import { normalizeAstarResponse } from "@/features/route/state/index.jsx"
import { routeErrorFromRequestError } from "@/features/voyagePlanner/routeError"
import { KNOTS_TO_MSEC } from "@/config/defaults"
import { nowUtc } from "@/utils/appNow"

import dayjs from "dayjs"
import utc from "dayjs/plugin/utc"
dayjs.extend(utc)

function parseErrorDetail(bodySnippet) {
  if (!bodySnippet) return null
  try {
    const parsed = JSON.parse(bodySnippet)
    const detail = parsed?.detail
    if (typeof detail === "string") return detail
    if (detail) return JSON.stringify(detail)
  } catch {
    return bodySnippet
  }
  return bodySnippet
}

export default async function runPlanrouteAnalyser({
  accessContext,
  setAccessContext,
  voyagePlannerPoints,
  calculatorDepartureTime,
  calculatorBasespeed,
  plannerVesselSettings,
  speedProfileData,
  consumptionProfileData,
  currentDraft = null,
  consumptionPrices = null,
  cii = null,
  fuelOptimisation = false,
  vesselId = null,
  windLimit,
  waveLimit,
  currentLimit,
  cwViaAreasGeojson,
  applyRnwSafety,
  applyEca = false,
  avoidHra = false,
  avoidJwla = false,
  avoidCaa = false,
  setLoadingRequest = () => {},
  setEtaResponse,
  setRouteConsumption,
  setPathfinderResponse,
  setEtaError = () => {},
  // When set, failures go to the Result step's error state instead of setEtaError.
  setRouteError = null,
  replaceRoute = () => {},
  setIsCalculating = () => {},
  vesselName = "Pathfinder",
  // Entity metadata for the store merge; tree-route recalcs pass the persisted
  // route's type so the replace doesn't relabel it as a draft.
  routeType = "Pathfinder",
  // Store slot this compute writes to; recalcs pass the edited draft's id.
  draftRouteId = -1,
  draftContext = null,
  // Fresh computes pass "planner"; recalcs omit so the draft keeps its origin.
  source = null,
  // json_request of the draft being edited; recalcs reuse those settings.
  previousJsonRequest = null,
  // Recalc of a draft that isn't the planner's own: keep its departure/speed, leave the Result step alone.
  ownTiming = false,
}) {
  const ownDeparture = ownTiming
    ? previousJsonRequest?.departure?.datetime_utc
    : null
  const nowMinute = nowUtc().startOf("minute")
  const effectiveDepartureTime =
    ownDeparture && dayjs.utc(ownDeparture).isValid()
      ? dayjs.utc(ownDeparture).isBefore(nowMinute)
        ? nowMinute
        : dayjs.utc(ownDeparture)
      : calculatorDepartureTime
  const ownSpeedMsec = ownTiming
    ? Number(previousJsonRequest?.vessel?.basespeed_msec)
    : NaN
  const effectiveBasespeed =
    ownSpeedMsec > 0 ? ownSpeedMsec / KNOTS_TO_MSEC : calculatorBasespeed

  // Map-edit recalcs run outside the planner and pass no fuel params —
  // recover them from the draft's previous request. That one is in astar
  // shape (see core apply_fuel_features_to_astar): prices/cii moved under
  // consumption/cii, fuel optimisation encoded in the weights. The previous
  // cii block is reused as is, so the rating year survives a recalc.
  const effectiveConsumptionPrices =
    consumptionPrices ?? previousJsonRequest?.consumption?.prices ?? null
  const effectiveCii = cii ?? previousJsonRequest?.cii ?? null
  const effectiveFuelOptimisation =
    fuelOptimisation || isFuelOptimisedRequest(previousJsonRequest)

  let data = buildCoreRequest({
    algorithm: ALGORITHM_PLANROUTE_ANALYSER,
    voyagePlannerPoints,
    calculatorDepartureTime: effectiveDepartureTime,
    calculatorBasespeed: effectiveBasespeed,
    plannerVesselSettings,
    speedProfileData,
    consumptionProfileData,
    currentDraft,
    consumptionPrices: effectiveConsumptionPrices,
    cii: effectiveCii,
    fuelOptimisation: effectiveFuelOptimisation,
    vesselId,
    windLimit: Number(windLimit ?? 0),
    waveLimit: Number(waveLimit ?? 0),
    currentLimit: Number(
      currentLimit ?? plannerVesselSettings?.currentLimit ?? 0,
    ),
    cwViaAreasGeojson,
    applyRnwSafety: !!applyRnwSafety,
    activeEca: !!applyEca,
    activeHra: !!avoidHra,
    activeJwla: !!avoidJwla,
    activeCustomAreas: !!avoidCaa,
  })

  if (previousJsonRequest) {
    const SETTINGS_KEYS = [
      "vessel",
      "wind_layer",
      "wave_layer",
      "current_layer",
      "profiles",
      "consumption",
      "config",
      "weights",
      "malus",
      "rnw_variables_active",
      "rnw_variables_vessel",
      "rnw_variables_voyage",
      "eca_variables_active",
      "hra_variables_active",
      "jwla_variables_active",
      "custom_areas_variables_active",
    ]
    try {
      const fc = JSON.parse(data)
      const props = fc?.features?.[0]?.properties
      if (props) {
        const requestConsumption = props.profiles?.consumption
        for (const key of SETTINGS_KEYS) {
          if (previousJsonRequest[key] !== undefined) {
            props[key] = previousJsonRequest[key]
          }
        }
        // Snapshots without a fuel model (imports) must not lose the
        // request's consumption profile together with the profiles block.
        if (
          requestConsumption != null &&
          previousJsonRequest.consumption == null &&
          props.profiles != null &&
          props.profiles.consumption == null
        ) {
          props.profiles = { ...props.profiles, consumption: requestConsumption }
        }
        // The previous vessel block is astar-shaped (id stripped by core);
        // re-add it so core can derive CII when the snapshot has none.
        if (Number(vesselId) > 0) {
          props.vessel = { ...props.vessel, id: Number(vesselId) }
        }
        data = JSON.stringify(fc)
      }
    } catch {
    }
  }

  setEtaError(null)
  setLoadingRequest(true)
  setIsCalculating(true)

  try {
    const responseJson = await corePost(
      "/services/planroute-analyser",
      data,
      accessContext,
      setAccessContext,
    )

    if (!ownTiming) setPathfinderResponse(responseJson)

    const normalizedRoute = normalizeAstarResponse(responseJson, {
      routeId: draftRouteId,
      routeType,
      vesselName: vesselName || "Pathfinder",
      source,
      draftContext,
      rawResponse: responseJson,
    })
    replaceRoute(draftRouteId, normalizedRoute)
    if (ownTiming) return true

    setRouteConsumption(
      computeTotalConsumption(
        responseJson,
        effectiveBasespeed,
        consumptionProfileData,
      ),
    )

    const responseWaypoints =
      normalizedRoute?.waypointsByRouteId?.[draftRouteId] ?? []
    const lastTimestamp =
      responseWaypoints[responseWaypoints.length - 1]?.timestamp
    if (lastTimestamp) {
      setEtaResponse(dayjs.utc(lastTimestamp).format("YYYY-MM-DD HH:mm:ss"))
    } else {
      setEtaResponse(null)
    }
    return true
  } catch (error) {
    console.error(
      "Planroute analyser request failed:",
      error,
      error?.bodySnippet || "",
    )
    const code = error.status || error.code
    if (setRouteError) {
      const routeError = routeErrorFromRequestError(
        error,
        nowUtc().toISOString(),
      )
      if (routeError) setRouteError(routeError)
      return false
    }
    const detail = parseErrorDetail(error?.bodySnippet)
    if (!code) {
      setEtaError("Service is unreachable. Please try again later.")
    } else if (code >= 500) {
      setEtaError(
        `${detail || "Server error"} (${code}). Please try again later.`,
      )
    } else if (code >= 400) {
      setEtaError(
        `${detail || "Invalid request"} (${code}). Please check voyage specifications!`,
      )
    }
    return false
  } finally {
    setLoadingRequest(false)
    setIsCalculating(false)
  }
}
